import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import {
  ANALYTICS_REPORTS,
  type AnalyticsDrillResult,
  type AnalyticsReport,
  type DropOff,
  type ExportFormat,
  type Proficiency,
  type ReportLibrary,
  type ReportTable,
  type ResolvedAnalyticsFilters,
  type SaveReportInput,
  type SavedReportView,
  type Scoreboard,
  type UpdateSavedReportInput,
} from '@nest-recruit/shared';
import { apiDelete, apiGet, apiPatch, apiPost, type ApiError } from '../../../lib/api-client';
import { readCommandCenterUrl, urlOf, type ReportUrl } from './reports-api';

/** F17.03 plus S29's role family, job and source — all in the address. */
export interface AnalyticsUrl extends ReportUrl {
  readonly roleFamily?: string;
  readonly jobId?: string;
  readonly source?: string;
}

export interface DropOffUrl extends AnalyticsUrl {
  /** N-61 lands on the process/experience withdrawals. */
  readonly section?: 'accountability' | 'sources';
}

export interface BuilderUrl extends AnalyticsUrl {
  readonly report?: AnalyticsReport;
  readonly saved?: string;
  readonly columns?: string;
}

export interface AnalyticsDrillUrl extends AnalyticsUrl {
  readonly report?: AnalyticsReport;
  readonly key?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SHORT_TEXT = /^[\w .:'&/-]{1,80}$/u;
const MAX_KEY = 160;
const MAX_COLUMNS = 1000;

export function readAnalyticsUrl(search: Readonly<Record<string, unknown>>): AnalyticsUrl {
  const text = (key: string, pattern: RegExp): string | undefined => {
    const value = search[key];
    return typeof value === 'string' && pattern.test(value) ? value : undefined;
  };
  const { section: _section, ...base } = readCommandCenterUrl(search);
  const roleFamily = text('roleFamily', SHORT_TEXT);
  const jobId = text('jobId', UUID);
  const source = text('source', SHORT_TEXT);
  return { ...base, ...(roleFamily ? { roleFamily } : {}), ...(jobId ? { jobId } : {}), ...(source ? { source } : {}) };
}

export function readDropOffUrl(search: Readonly<Record<string, unknown>>): DropOffUrl {
  const section = search.section === 'accountability' || search.section === 'sources' ? search.section : undefined;
  return { ...readAnalyticsUrl(search), ...(section ? { section } : {}) };
}

export function readBuilderUrl(search: Readonly<Record<string, unknown>>): BuilderUrl {
  const report = ANALYTICS_REPORTS.find((value) => value === search.report);
  const saved = typeof search.saved === 'string' && UUID.test(search.saved) ? search.saved : undefined;
  const columns = typeof search.columns === 'string' ? search.columns.slice(0, MAX_COLUMNS) : undefined;
  return { ...readAnalyticsUrl(search), ...(report ? { report } : {}), ...(saved ? { saved } : {}), ...(columns ? { columns } : {}) };
}

export function readAnalyticsDrillUrl(search: Readonly<Record<string, unknown>>): AnalyticsDrillUrl {
  const report = ANALYTICS_REPORTS.find((value) => value === search.report);
  const key = typeof search.key === 'string' ? search.key.slice(0, MAX_KEY) : undefined;
  return { ...readAnalyticsUrl(search), ...(report ? { report } : {}), ...(key ? { key } : {}) };
}

/** The filters as applied, back into the address form. */
export function analyticsUrlOf(filters: ResolvedAnalyticsFilters): AnalyticsUrl {
  return {
    ...urlOf(filters),
    ...(filters.roleFamily ? { roleFamily: filters.roleFamily } : {}),
    ...(filters.jobId ? { jobId: filters.jobId } : {}),
    ...(filters.source ? { source: filters.source } : {}),
  };
}

function queryOf(url: AnalyticsUrl): Record<string, string | undefined> {
  return {
    period: url.period,
    from: url.from,
    to: url.to,
    ventureId: url.ventureId,
    departmentId: url.departmentId,
    recruiterUserId: url.recruiterUserId,
    roleFamily: url.roleFamily,
    jobId: url.jobId,
    source: url.source,
  };
}

function searchOf(values: Record<string, string | undefined>): string {
  const params = new URLSearchParams();
  for (const [name, value] of Object.entries(values)) if (value) params.set(name, value);
  return params.toString();
}

/** The records behind one S29 figure (F17.02). */
export function analyticsDrillHref(filters: ResolvedAnalyticsFilters, report: AnalyticsReport, key: string): string {
  return `/reports/drill?${searchOf({ report, key, ...queryOf(analyticsUrlOf(filters)) })}`;
}

/** F17.04 — a real download link, so it works with the browser's own save and new tab. */
export function exportHref(report: AnalyticsReport, url: AnalyticsUrl, format: ExportFormat, columns: readonly string[], savedReportId?: string): string {
  return `/api/v1/reports/export?${searchOf({ report, format, columns: columns.join(',') || undefined, savedReportId, ...queryOf(url) })}`;
}

export const PROFICIENCY_KEY = ['proficiency'] as const;
export const SCOREBOARD_KEY = ['scoreboard'] as const;
export const DROP_OFF_KEY = ['drop-off'] as const;
export const REPORT_TABLE_KEY = ['report-table'] as const;
export const ANALYTICS_DRILL_KEY = ['analytics-drill'] as const;
export const REPORT_LIBRARY_KEY = ['report-library'] as const;
export const SAVED_REPORT_KEY = ['saved-report'] as const;

/** A filter change swaps the figures in place (§5a rule 2). */
export function useProficiency(url: AnalyticsUrl, isEnabled: boolean): UseQueryResult<Proficiency, ApiError> {
  return useQuery({ queryKey: [...PROFICIENCY_KEY, url], queryFn: () => apiGet<Proficiency>('/reports/proficiency', { query: queryOf(url) }), enabled: isEnabled, placeholderData: (previous) => previous });
}

export function useScoreboard(url: AnalyticsUrl, isEnabled: boolean): UseQueryResult<Scoreboard, ApiError> {
  return useQuery({ queryKey: [...SCOREBOARD_KEY, url], queryFn: () => apiGet<Scoreboard>('/reports/scoreboard', { query: queryOf(url) }), enabled: isEnabled, placeholderData: (previous) => previous });
}

export function useDropOff(url: AnalyticsUrl, isEnabled: boolean): UseQueryResult<DropOff, ApiError> {
  return useQuery({ queryKey: [...DROP_OFF_KEY, url], queryFn: () => apiGet<DropOff>('/reports/drop-off', { query: queryOf(url) }), enabled: isEnabled, placeholderData: (previous) => previous });
}

export function useAnalyticsDrill(url: AnalyticsDrillUrl, isEnabled: boolean): UseQueryResult<AnalyticsDrillResult, ApiError> {
  return useQuery({
    queryKey: [...ANALYTICS_DRILL_KEY, url],
    queryFn: () => apiGet<AnalyticsDrillResult>('/reports/analytics-drill', { query: { ...queryOf(url), report: url.report, key: url.key } }),
    enabled: isEnabled && url.report !== undefined,
    placeholderData: (previous) => previous,
  });
}

export function useReportTable(report: AnalyticsReport | undefined, url: AnalyticsUrl, columns: readonly string[]): UseQueryResult<ReportTable, ApiError> {
  return useQuery({
    queryKey: [...REPORT_TABLE_KEY, report, url, columns],
    queryFn: () => apiGet<ReportTable>('/reports/table', { query: { ...queryOf(url), report, columns: columns.join(',') || undefined } }),
    enabled: report !== undefined,
    placeholderData: (previous) => previous,
  });
}

export function useReportLibrary(): UseQueryResult<ReportLibrary, ApiError> {
  return useQuery({ queryKey: REPORT_LIBRARY_KEY, queryFn: () => apiGet<ReportLibrary>('/reports/library') });
}

export function useSavedReport(id: string | undefined): UseQueryResult<SavedReportView, ApiError> {
  return useQuery({ queryKey: [...SAVED_REPORT_KEY, id], queryFn: () => apiGet<SavedReportView>(`/reports/saved/${id ?? ''}`), enabled: id !== undefined });
}

/** Saving shows in the library at once; a failure takes it back out (§5a rule 1). */
export function useSaveReport(): UseMutationResult<SavedReportView, ApiError, SaveReportInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input) => apiPost<SavedReportView>('/reports/saved', input),
    onSettled: () => client.invalidateQueries({ queryKey: REPORT_LIBRARY_KEY }),
  });
}

export function useUpdateSavedReport(): UseMutationResult<SavedReportView, ApiError, { readonly id: string; readonly input: UpdateSavedReportInput }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }) => apiPatch<SavedReportView>(`/reports/saved/${id}`, input),
    onSuccess: (view) => client.setQueryData([...SAVED_REPORT_KEY, view.id], view),
    onSettled: () => client.invalidateQueries({ queryKey: REPORT_LIBRARY_KEY }),
  });
}

export function useDeleteSavedReport(): UseMutationResult<void, ApiError, { readonly id: string; readonly version: number }, { readonly previous: ReportLibrary | undefined }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }) => apiDelete<void>(`/reports/saved/${id}?version=${version}`),
    onMutate: async ({ id }) => {
      await client.cancelQueries({ queryKey: REPORT_LIBRARY_KEY });
      const previous = client.getQueryData<ReportLibrary>(REPORT_LIBRARY_KEY);
      if (previous) client.setQueryData<ReportLibrary>(REPORT_LIBRARY_KEY, { ...previous, saved: previous.saved.filter((row) => row.id !== id) });
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) client.setQueryData(REPORT_LIBRARY_KEY, context.previous);
    },
    onSettled: () => client.invalidateQueries({ queryKey: REPORT_LIBRARY_KEY }),
  });
}
